import { useEffect, useMemo, useRef, useState } from 'react';
import {
  EMITTER_CONSTANTS,
  EVENT_COUNT_CONVERT,
  KEY_RECOIL,
  STRIP_AFIB_THUMBNAIL_INFO,
} from '@/shared/config';
import { useEmitter, usePaginationKeyboard } from '@/shared/lib/customHooks';
import { createOnChangePageString } from '@/shared/lib/parsePageInput';
import {
  BEAT_HR_TAB_ID,
  selectEventFilter,
  selectPageIndex,
  selectSpecialEventCount,
  useReportStore,
} from '@/entities/report';
import { cancelAllAPI } from '@/shared/lib/apiHelpers';
import { useEventsInfoBeatHr } from '@/entities/beat';
import { isAppliedFilterEvent } from '@/entities/event';
import type { AfibPaginationContainerProps } from './afibPaginationContainer';

/**
 * Backs the pagination bar under the AFib event thumbnails. Derives the total strip count from the filtered special-event counts or the tab's event info, computes the page count and the disabled state of both arrows, moves the page on click, Enter or blur while flagging out-of-range input, and also answers the Z and X keys and the beats and HR next and previous emitter events.
 *
 * @param props - the active rhythm event type and the disabled flag for the bar.
 * @returns An object carrying the arrow state and handlers, the page text with its input size and error flag, and the maximum page number.
 */
const useAfibPaginationContainer = ({ activeButton, isDisabled }: AfibPaginationContainerProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the strip pagination to another page. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const specialEventCount = useReportStore(selectSpecialEventCount(keyRecoil));
  const eventsInfo = useEventsInfoBeatHr();
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  const [pageString, setPageString] = useState(pageIndex.index + 1);

  const [inputSize, setInputSize] = useState(String(pageIndex.index + 1).length + 1);
  const [isError, setIsError] = useState(false);
  const pageStringRef = useRef(pageString);
  const maxPageNumberRef = useRef<number>();

  const totalStrip = useMemo(() => {
    if (isAppliedFilterEvent(eventFilter, activeButton)) {
      return (specialEventCount as any)?.[(EVENT_COUNT_CONVERT as any)[activeButton]]?.count;
    }
    return (eventsInfo as any)[(EVENT_COUNT_CONVERT as any)[activeButton]]?.count;
  }, [eventFilter, specialEventCount, activeButton, eventsInfo]);

  const maxPageNumber = totalStrip
    ? Math.ceil(totalStrip / STRIP_AFIB_THUMBNAIL_INFO.stripDisplayLimit)
    : 1;
  maxPageNumberRef.current = maxPageNumber;

  // *: Always display pagination
  // const isShowPagination = totalStrip > stripDisplayLimit;
  const isShowPagination = true;
  const isDisabledNext =
    (pageIndex.index + 1) * STRIP_AFIB_THUMBNAIL_INFO.stripDisplayLimit >= totalStrip;
  const isDisabledPrev = pageIndex.index === 0;

  /**
   * Moves the strip thumbnails back one page. Cancels every in-flight API call before lowering the page index.
   *
   * @returns Nothing.
   */
  const onClickPrevious = () => {
    cancelAllAPI();
    setPageIndex({ index: pageIndex.index - 1 });
  };

  /**
   * Advances the strip thumbnails one page. Cancels every in-flight API call before raising the page index.
   *
   * @returns Nothing.
   */
  const onClickNext = () => {
    cancelAllAPI();
    setPageIndex({ index: pageIndex.index + 1 });
  };

  const onChangePageString = createOnChangePageString({
    setIsError,
    setPageString,
    pageStringRef,
    setInputSize,
  });

  /**
   * Commits the typed page number once the input loses focus. Applies the page when it parses and falls within the page count, replacing the stored index only if it actually changed, and raises the error flag otherwise.
   *
   * @returns Nothing.
   */
  const onBlur = () => {
    if (pageString) {
      const pageNumber = Number(pageString);
      if (pageNumber && pageNumber <= maxPageNumber) {
        const newPageIndex = Number(pageString) - 1;
        setPageIndex((prev) => (newPageIndex !== prev.index ? { index: newPageIndex } : prev));
      } else {
        setIsError(true);
      }
    }
  };

  const { onKeyDown } = usePaginationKeyboard({
    tabZoneId: BEAT_HR_TAB_ID,
    isDisabled,
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    pageStringRef,
    maxPageNumberRef,
    setPageIndex,
    setIsError,
    deps: [totalStrip, pageIndex, isDisabled],
  });

  useEmitter(
    EMITTER_CONSTANTS.BEAT_HR_NEXT,
    () => {
      if (isShowPagination && !isDisabledNext) {
        onClickNext();
      }
    },
    [pageIndex, totalStrip],
  );

  useEmitter(
    EMITTER_CONSTANTS.BEAT_HR_PREV,
    () => {
      if (isShowPagination && !isDisabledPrev) {
        onClickPrevious();
      }
    },
    [pageIndex, totalStrip],
  );

  useEffect(() => {
    setPageString(pageIndex.index + 1);
    setInputSize(String(pageIndex.index + 1).length + 1);
  }, [pageIndex]);

  useEffect(() => {
    if (maxPageNumber <= pageIndex.index) {
      setPageIndex({ index: maxPageNumber - 1 });
    }
  }, [maxPageNumber, pageIndex]);

  return {
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    maxPageNumber,
    pageString,
    onChangePageString,
    onKeyDown,
    onBlur,
    inputSize,
    isError,
  };
};

export default useAfibPaginationContainer;
